import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from 'react-select/async';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { cercaFraIClienti } from '../../../../components/Util/clientiDellaStruttura';
import { segnalaMancanti } from '../../../../components/Util/campiMancanti';

const LISTA_SOCI = '/struttura/soci/list';

export const AggiungiSocio = () => {

	const navigate = useNavigate();

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');
	const [esitoShowed, setEsitoShowed] = useState(false);
	const [esitoText, setEsitoText] = useState('');

	const [selectedUser, setSelectedUser] = useState(null);
	const [perJunior, setPerJunior] = useState(false);
	const [juniors, setJuniors] = useState([]);
	const [selectedJunior, setSelectedJunior] = useState(null);

	const [dateStart, setDateStart] = useState('');
	const [dateEnd, setDateEnd] = useState('');

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	const caricaJuniors = useCallback(async (playerId) => {
		const auth = getAuth();
		if (!auth || !playerId) return;

		const apiClient = new MyApiClient();
		const risultato = await apiClient.getJuniorsByParent(auth.token, playerId);
		const elenco = Array.isArray(risultato) ? risultato : (risultato?.items || []);
		// L'endpoint restituisce givenName/familyName, non un `name` gia' composto.
		setJuniors(elenco.map(j => ({
			value: j.userId,
			label: [j.givenName, j.familyName].filter(Boolean).join(' ') || j.userId
		})));
	}, []);

	const handleUserSelect = (opzione) => {
		setSelectedUser(opzione);
		setSelectedJunior(null);
		setJuniors([]);
		if (opzione) caricaJuniors(opzione.value);
	};

	const loadUserOptions = async (inputValue) => {
		if (inputValue.length < 3) return [];

		const auth = getAuth();
		if (!auth) return [];

		// Solo fra i clienti della struttura (03/11/2026): vedi clientiDellaStruttura.js
		return cercaFraIClienti(auth.token, inputValue);
	};

	/** userId a cui intestare la posizione: il junior se richiesto, altrimenti il player. */
	const destinatario = () => (perJunior ? selectedJunior?.value : selectedUser?.value);

	/** Player (ed eventuale junior) mancanti, come elenco per segnalaMancanti. */
	const mancantiPlayer = () => {
		const m = [];
		if (!selectedUser) m.push({ campo: 'socio-player', messaggio: 'Manca il player' });
		else if (perJunior && !selectedJunior) m.push({ campo: 'socio-junior-scelta', messaggio: 'Manca il junior da inserire' });
		return m;
	};

	const salvaManuale = async () => {
		const mancanti = mancantiPlayer();
		if (!dateStart) mancanti.push({ campo: 'socio-data-inizio', messaggio: 'Manca la data di inizio' });
		if (!dateEnd) mancanti.push({ campo: 'socio-data-fine', messaggio: 'Manca la data di fine' });
		else if (dateStart && dateStart > dateEnd) mancanti.push({ campo: 'socio-data-fine', messaggio: 'La data di fine non può essere precedente alla data di inizio' });
		if (segnalaMancanti(mancanti) > 0) return;

		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.addClubMember(auth.token, auth.clubId, {
			userId: destinatario(),
			dateStart,
			dateEnd
		});
		setLoadingShowed(false);

		if (risultato && risultato.error) {
			mostraErrore("Si è verificato un errore durante il salvataggio: " + risultato.error);
			return;
		}

		setEsitoText("Il player è stato inserito nel libro soci.");
		setEsitoShowed(true);
	};

	// La candidatura non crea un socio: crea una membership in attesa, che finisce
	// fra le richieste. Il progressivo viene assegnato solo alla conferma.
	const inviaCandidatura = async () => {
		if (segnalaMancanti(mancantiPlayer()) > 0) return;

		const auth = getAuth();
		if (!auth) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.inviteUser(auth.token, auth.clubId, {
			userInvitations: [{ userId: destinatario(), membershipRank: 'MEMBER' }]
		});
		setLoadingShowed(false);

		if (risultato && risultato.error) {
			mostraErrore("Non è stato possibile inviare la candidatura: " + risultato.error);
			return;
		}

		setEsitoText("La candidatura è stata inviata: il player resta in attesa fra le richieste finché il consiglio direttivo non la conferma.");
		setEsitoShowed(true);
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={esitoShowed} centered>
				<Modal.Header>
					<Modal.Title>Operazione completata</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{esitoText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => navigate(LISTA_SOCI)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<div style={{ maxWidth: '920px' }}>
				<div className="card">
					<div className="card-header">
						<h4 className="card-title">Seleziona il player</h4>
					</div>
					<div className="card-body">
						<Form.Label htmlFor="socio-player">Cerca player *</Form.Label>
						<AsyncSelect
							inputId="socio-player"
							value={selectedUser}
							onChange={handleUserSelect}
							loadOptions={loadUserOptions}
							placeholder="Inizia a digitare per cercare un player (minimo 3 caratteri)…"
							noOptionsMessage={({ inputValue }) =>
								inputValue.length < 3
									? "Digita almeno 3 caratteri per iniziare la ricerca"
									: "Nessun cliente con questo nome: aggiungilo prima in Clienti"
							}
						/>
						<div className="text-muted mt-2" style={{ fontSize: '13px' }}>
							Cerca per nome o cognome tra i clienti della struttura.
						</div>

						<Form.Check
							type="checkbox"
							id="socio-junior"
							className="mt-4"
							label="Seleziona Junior"
							checked={perJunior}
							onChange={(e) => { setPerJunior(e.target.checked); setSelectedJunior(null); }}
							disabled={!selectedUser}
						/>
						{perJunior && (
							<div className="mt-3">
								<Form.Label htmlFor="socio-junior-scelta">Junior *</Form.Label>
								<Select
									inputId="socio-junior-scelta"
									value={selectedJunior}
									onChange={setSelectedJunior}
									options={juniors}
									placeholder={juniors.length ? "Seleziona junior…" : "Questo player non ha junior collegati"}
									isDisabled={!juniors.length}
								/>
							</div>
						)}
					</div>
				</div>

				<h4 className="card-title" style={{ margin: '22px 0 12px' }}>Scegli come inserire il player</h4>

				<Row>
					<Col lg={6} className="mb-4 d-flex">
						<div className="card w-100 mb-0">
							<div className="card-header">
								<h4 className="card-title">Inserimento manuale</h4>
							</div>
							<div className="card-body d-flex flex-column">
								<p className="text-muted">Seleziona il periodo per inserire manualmente il player nel libro soci.</p>

								<Row>
									<Col md={6} className="mb-3">
										<Form.Label htmlFor="socio-data-inizio">Data inizio *</Form.Label>
										<Form.Control id="socio-data-inizio" type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
									</Col>
									<Col md={6} className="mb-3">
										<Form.Label htmlFor="socio-data-fine">Data fine *</Form.Label>
										<Form.Control id="socio-data-fine" type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
									</Col>
								</Row>

								<div className="flex-grow-1" />
								<div className="d-flex justify-content-end gap-4 mt-4">
									<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_SOCI)}>Annulla</Button>
									<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salvaManuale}>Salva</Button>
								</div>
							</div>
						</div>
					</Col>

					<Col lg={6} className="mb-4 d-flex">
						<div className="card w-100 mb-0">
							<div className="card-header">
								<h4 className="card-title">Candidatura</h4>
							</div>
							<div className="card-body d-flex flex-column">
								<p className="text-muted">
									Il player sarà candidato a diventare socio e inserito nelle richieste al consiglio direttivo.
								</p>

								<div className="flex-grow-1" />
								<div className="d-flex justify-content-end gap-4 mt-4">
									<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_SOCI)}>Annulla</Button>
									<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={inviaCandidatura}>Invia</Button>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</div>
		</>
	);
};

export default AggiungiSocio;
